import React, { useState } from 'react';
import {
  Box,
  VStack,
  Avatar,
  Text,
  Badge,
  Divider,
  Link,
  Button,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useDisclosure,
  Flex,
  IconButton,
  useToast
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { 
  FaChartLine, 
  FaGamepad, 
  FaExchangeAlt, 
  FaHandHoldingUsd, 
  FaTicketAlt, 
  FaGift, 
  FaStore, 
  FaCog, 
  FaSignOutAlt,
  FaArrowLeft,
  FaEdit,
  FaCamera,
  FaUser,
  FaSave,
  FaCoins,
  FaUsers
} from 'react-icons/fa';
import { GiPirateFlag } from 'react-icons/gi';
import { useColorModeValue } from '@chakra-ui/react';

// Get section color based on section name
const getSectionColor = (section) => {
  switch(section) {
    case 'dashboard': return 'pink.400';
    case 'games': return 'purple.400';
    case 'nftlootboxes': return 'blue.400';
    case 'raffles': return 'cyan.400';
    case 'pawnshop': return 'teal.400';
    case 'marketplace': return 'green.400';
    case 'dex': return 'yellow.400';
    case 'staking': return 'orange.400';
    case 'dao': return 'red.400';
    case 'affiliate': return 'red.700';
    default: return 'gray.400';
  }
};

// Get hover background color based on section name
const getSectionBgColor = (section) => {
  switch(section) {
    case 'dashboard': return 'rgba(214, 51, 108, 0.1)';
    case 'games': return 'rgba(128, 90, 213, 0.1)';
    case 'nftlootboxes': return 'rgba(66, 153, 225, 0.1)';
    case 'raffles': return 'rgba(76, 201, 240, 0.1)';
    case 'pawnshop': return 'rgba(49, 151, 149, 0.1)';
    case 'marketplace': return 'rgba(72, 187, 120, 0.1)';
    case 'dex': return 'rgba(236, 201, 75, 0.1)';
    case 'staking': return 'rgba(237, 137, 54, 0.1)';
    case 'dao': return 'rgba(245, 101, 101, 0.1)';
    case 'affiliate': return 'rgba(185, 28, 28, 0.15)';
    default: return 'rgba(160, 174, 192, 0.1)';
  }
};

// SidebarItem component
const SidebarItem = ({ icon, label, isActive, onClick, section }) => {
  const sectionColor = getSectionColor(section);
  const sectionBgColor = getSectionBgColor(section);
  
  return (
    <Box 
      as="button"
      onClick={onClick}
      w="full"
      py={2}
      px={3}
      borderRadius="md"
      bg={isActive ? sectionBgColor : "transparent"}
      color={isActive ? sectionColor : "gray.400"}
      fontWeight={isActive ? "bold" : "normal"}
      textAlign="left"
      transition="all 0.2s"
      _hover={{
        bg: sectionBgColor,
        color: isActive ? sectionColor : sectionColor
      }}
    >
      <Box display="flex" alignItems="center">
        <Icon as={icon} mr={3} boxSize={5} color={isActive ? sectionColor : "gray.400"} />
        <Text>{label}</Text>
      </Box>
    </Box>
  );
};

const ProfileSidebar = ({ 
  activeSection, 
  setActiveSection, 
  userStats,
  goldColor,
  onLogout
}) => {
  // Custom navigation function to ensure consistent behavior
  const navigateWithoutReload = (path) => {
    // Only update if the path is different from current path
    if (path !== window.location.pathname) {
      // Use window.history.pushState for consistent navigation without page reload
      window.history.pushState({}, '', path);
      
      // Use a custom event instead of popstate to avoid triggering unnecessary re-renders
      const navigationEvent = new CustomEvent('navigation-change', { detail: { path } });
      window.dispatchEvent(navigationEvent);
    }
  };
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [profileData, setProfileData] = useState({
    username: userStats?.username || "PalmHeist User",
    avatar: userStats?.avatar || "",
    bio: userStats?.bio || "Crypto enthusiast and NFT collector",
    level: userStats?.level || 42,
    twitter: userStats?.twitter || "@palmheistuser",
    discord: userStats?.discord || "palmheistuser#1234"
  });
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData({
      ...profileData,
      [name]: value
    });
  };
  
  const handleSaveProfile = () => {
    // Here you would typically save to backend/blockchain
    toast({
      title: "Profile updated",
      description: "Your profile has been successfully updated.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
    onClose();
  };
  
  const handleAvatarChange = () => {
    // This would typically open a file picker
    // For now, we'll just simulate with a random avatar
    const avatarId = Math.floor(Math.random() * 1000);
    setProfileData({
      ...profileData,
      avatar: `https://avatars.dicebear.com/api/human/${avatarId}.svg`
    });
  };
  
  return (
    <Box 
      position="absolute" 
      top="84px" 
      left="0" 
      w="250px" 
      bg="rgba(13, 16, 25, 0.95)" 
      border="1px solid" 
      borderColor="gray.800"
      borderRadius="15px"
      boxShadow="0 4px 12px rgba(0, 0, 0, 0.3)"
      overflowY="hidden"
      p={5}
      m={4}
      mt={0}
      h="auto"
    >
      <VStack spacing={5} align="stretch">
        {/* Profile Section */}
        <VStack spacing={3} align="center">
          <Box position="relative">
            <Avatar 
              size="xl" 
              name={profileData.username} 
              src={profileData.avatar} 
              border="3px solid"
              borderColor={goldColor}
            />
          </Box>
          <Box textAlign="center">
            <Text fontWeight="bold" fontSize="xl">{profileData.username}</Text>
          </Box>
          <Text fontSize="sm" color="gray.400" textAlign="center" mt={1}>
            {profileData.bio}
          </Text>
        </VStack>
        
        <Divider borderColor="gray.800" my={1} />
        
        {/* Navigation Section */}
        <VStack spacing={1} align="stretch">
          <SidebarItem 
            icon={FaChartLine} 
            label="Dashboard" 
            isActive={activeSection === 'dashboard'} 
            onClick={() => {
              setActiveSection('dashboard');
              // Use consistent navigation approach
              navigateWithoutReload('/modular-profile/dashboard');
            }}
            section="dashboard"
          />
          <SidebarItem 
            icon={FaGamepad} 
            label="Games" 
            isActive={activeSection === 'games'} 
            onClick={() => {
              setActiveSection('games');
              // Use the navigateWithoutReload function for consistent navigation
              navigateWithoutReload('/modular-profile/games/island-map');
            }}
            section="games"
          />
          <SidebarItem 
            icon={FaGift} 
            label="NFT Lootboxes" 
            isActive={activeSection === 'nftlootboxes'} 
            onClick={() => {
              setActiveSection('nftlootboxes');
              // Use consistent navigation approach
              navigateWithoutReload('/modular-profile/nftlootboxes');
            }}
            section="nftlootboxes"
          />
          <SidebarItem 
            icon={FaTicketAlt} 
            label="Raffles" 
            isActive={activeSection === 'raffles'} 
            onClick={() => {
              setActiveSection('raffles');
              // Use consistent navigation approach
              navigateWithoutReload('/modular-profile/raffles');
            }}
            section="raffles"
          />
          <SidebarItem 
            icon={FaHandHoldingUsd} 
            label="Pawn Shop" 
            isActive={activeSection === 'pawnshop'} 
            onClick={() => {
              setActiveSection('pawnshop');
              // Use consistent navigation approach
              navigateWithoutReload('/modular-profile/pawnshop');
            }}
            section="pawnshop"
          />
          <SidebarItem 
            icon={FaStore} 
            label="Marketplace" 
            isActive={activeSection === 'marketplace'} 
            onClick={() => {
              setActiveSection('marketplace');
              // Use consistent navigation approach
              navigateWithoutReload('/modular-profile/marketplace');
            }}
            section="marketplace"
          />
          <SidebarItem 
            icon={FaExchangeAlt} 
            label="DEX" 
            isActive={activeSection === 'dex'} 
            onClick={() => {
              setActiveSection('dex');
              // Use consistent navigation approach
              navigateWithoutReload('/modular-profile/dex');
            }}
            section="dex"
          />
          <SidebarItem 
            icon={FaCoins} 
            label="Staking" 
            isActive={activeSection === 'staking'} 
            onClick={() => {
              setActiveSection('staking');
              // Use consistent navigation approach
              navigateWithoutReload('/modular-profile/staking');
            }}
            section="staking"
          />
          <SidebarItem 
            icon={GiPirateFlag} 
            label="DAO" 
            isActive={activeSection === 'dao'} 
            onClick={() => {
              setActiveSection('dao');
              // Use consistent navigation approach
              navigateWithoutReload('/modular-profile/dao');
            }}
            section="dao"
          />
          <SidebarItem 
            icon={FaUsers} 
            label="Affiliate" 
            isActive={activeSection === 'affiliate'} 
            onClick={() => {
              setActiveSection('affiliate');
              // Use consistent navigation approach
              navigateWithoutReload('/modular-profile/affiliate');
            }}
            section="affiliate"
          />
        </VStack>
        
        <Divider borderColor="gray.800" my={1} />
        
        {/* Settings & Logout */}
        <VStack spacing={1} align="stretch" mt="auto">
          <SidebarItem 
            icon={FaCog} 
            label="Settings" 
            isActive={activeSection === 'settings'} 
            onClick={() => {
              setActiveSection('settings');
              // Use consistent navigation approach
              navigateWithoutReload('/modular-profile/settings');
            }}
            section="settings"
          />
          <Box 
            as="button"
            w="full"
            py={2}
            px={3}
            borderRadius="md"
            color="red.400"
            textAlign="left"
            transition="all 0.2s"
            _hover={{
              bg: "rgba(254, 178, 178, 0.1)",
              color: "red.300"
            }}
            onClick={onLogout}
          >
            <Box display="flex" alignItems="center">
              <Icon as={FaSignOutAlt} mr={3} boxSize={5} />
              <Text>Logout</Text>
            </Box>
          </Box>
        </VStack>
      </VStack>
      
      {/* Edit Profile Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent bg="rgba(23, 25, 35, 0.95)" borderColor="purple.500" borderWidth="1px">
          <ModalHeader color="white">Edit Profile</ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody pb={6}>
            <FormControl>
              <FormLabel color="gray.300">Username</FormLabel>
              <Input 
                name="username" 
                value={profileData.username} 
                onChange={handleInputChange} 
                bg="rgba(13, 16, 25, 0.7)"
                borderColor="gray.600"
                color="white"
              />
            </FormControl>

            <FormControl mt={4}>
              <FormLabel color="gray.300">Bio</FormLabel>
              <Textarea 
                name="bio" 
                value={profileData.bio} 
                onChange={handleInputChange} 
                bg="rgba(13, 16, 25, 0.7)"
                borderColor="gray.600"
                color="white"
                resize="vertical"
                rows={3}
              />
            </FormControl>
            
            <FormControl mt={4}>
              <FormLabel color="gray.300">Twitter Handle</FormLabel>
              <Input 
                name="twitter" 
                value={profileData.twitter} 
                onChange={handleInputChange} 
                bg="rgba(13, 16, 25, 0.7)"
                borderColor="gray.600"
                color="white"
              />
            </FormControl>
            
            <FormControl mt={4}>
              <FormLabel color="gray.300">Discord Username</FormLabel>
              <Input 
                name="discord" 
                value={profileData.discord} 
                onChange={handleInputChange} 
                bg="rgba(13, 16, 25, 0.7)"
                borderColor="gray.600"
                color="white"
              />
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="purple" mr={3} leftIcon={<FaSave />} onClick={handleSaveProfile}>
              Save
            </Button>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ProfileSidebar;
